<script lang="ts">
	import { afterUpdate, createEventDispatcher, onMount, tick } from 'svelte';
	import { format } from 'svelte-i18n';

	import { capFirstChar } from '../../helpers/utilities';

	import { Text, Input, Stack, Collapse, Alert } from '@Shelly/shelly-design-system';
	import type { SelectCardPropsTypes } from '@Shelly/shelly-design-system';
	import Cards from './Cards.svelte';
	import type { Card, ListType } from './SelectList.types';
	import { isCardSelect } from './SelectList.helpers';
	import NoSearchResults from '../Lists/NoSearchResults.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	const dispatch = createEventDispatcher();

	let className = 'mb-m ';
	export { className as class };
	export let title: string = '';
	export let titleHighlight: string | number = '';
	export let description = '';
	export let withSearch = true;
	export let list: ListType = [];
	export let variant: SelectCardPropsTypes['variant'] = 'checkbox';
	export let allCardsRefs: { [key: string | number]: HTMLElement } = {};
	export let firstSelectedRef: HTMLElement = null;
	export let selectCardClass = '';
	export let selectedCardsId: { id: string | number }[] = [];
	export let showTitleSearchSection = true;
	export let showTitle = true;
	export let missingDevicesAlertHeading: string = '';
	export let hideSelectListCustomCard = false;

	let filterValue = '';
	let cacheFilterValue = filterValue;
	let roomsExpanded = {};
	const fullSearchClass = 'width-full mb-m';
	$: searchClass = $showNewLayout || !showTitle ? fullSearchClass : 'width-lg-l width-sm-full mb-m';

	$: filteredList = filterList(list, filterValue);

	onMount(() => {
		loadExpandedRooms();
	});

	$: if (Object.keys(allCardsRefs).length > 0) {
		const firstSelectedCardID = getFirstSelectedCardID();
		firstSelectedRef = allCardsRefs[firstSelectedCardID];
	}

	const getFirstSelectedCardID = () => {
		if (isCardArray(list)) {
			for (const card of list) {
				if (isCardSelect(card, selectedCardsId)) {
					return card.id;
				}
			}
		} else {
			for (const room of list) {
				for (const card of room.cards) {
					if (isCardSelect(card, selectedCardsId)) {
						return card.id;
					}
				}
			}
		}
	};

	// beforeUpdate(() => {});

	const loadExpandedRooms = () => {
		if (!isCardArray(list)) {
			roomsExpanded = list.reduce(
				(o, room) => ({
					...o,
					[room.roomID]: room.cards.some((card) => isCardSelect(card, selectedCardsId)),
				}),
				{},
			);
		}
	};

	const isCardArray = (data: ListType): data is Card[] => {
		return Array.isArray(data) && data.length > 0 && !data[0].roomID;
	};

	const filterList = (list: ListType, filter: string) => {
		if (!withSearch) {
			return list;
		}

		if (filter === '') {
			if (cacheFilterValue !== '') {
				cacheFilterValue = filter;
				loadExpandedRooms();
			}

			return list;
		}

		cacheFilterValue = filter;
		const filterToLowerCase = filter.trim().toLowerCase();

		if (isCardArray(list)) {
			return list.filter((card) => card.name.toLowerCase().includes(filterToLowerCase));
		}

		const filteredData: ListType = [];

		list.forEach((room) => {
			const filteredCards = room.cards.filter((card) =>
				card.name.toLowerCase().includes(filterToLowerCase),
			);

			const cardsMatches = filteredCards.length > 0;

			if (cardsMatches) {
				roomsExpanded[room.roomID] = true;

				filteredData.push({
					...room,
					cards: filteredCards,
				} as any);
			} else {
				roomsExpanded[room.roomID] = false;
			}
		});

		return filteredData;
	};

	const handleCardClick = (card: Card) => {
		dispatch('click', card);
	};
</script>

{#if showTitleSearchSection}
	<Stack
		class={className}
		gap="s"
		direction="column"
		directionMobile="row"
		align="between"
		alignMobile="none"
	>
		{#if showTitle}
			<div class="d-flex flex-direction-column justify-content-center">
				<Text element="h2" noWrap>
					{title}
					{#if titleHighlight !== ''}
						<span class="text-primary">{titleHighlight}</span>
					{/if}
				</Text>
				{#if description}
					<Text styleType="body-small" class="mt-s">
						{description}
					</Text>
				{/if}
			</div>
		{/if}
		{#if withSearch && !$showNewLayout}
			<Input
				a11yProps={{
					iconButtonAriaLabel: $format('input_search_with_clear_button', {
						default: 'Input search with clear button',
					}),
				}}
				type="text"
				label={capFirstChar($format('search'))}
				icon="fas fa-search"
				class={searchClass}
				clearButton
				bind:value={filterValue}
				id="input-rg4ab4e"
				aria-labelledby="input-rg4ab4e"
			/>
		{/if}
	</Stack>
	{#if withSearch && $showNewLayout}
		<Input
			a11yProps={{
				iconButtonAriaLabel: $format('input_search_with_clear_button', {
					default: 'Input search with clear button',
				}),
			}}
			type="text"
			label={capFirstChar($format('search'))}
			icon="fas fa-search"
			class={searchClass}
			clearButton
			bind:value={filterValue}
			id="input-rg4ab4e"
			aria-labelledby="input-rg4ab4e"
		/>
	{/if}
{/if}

{#if filteredList?.length > 0}
	{#if isCardArray(filteredList)}
		<Cards
			bind:allCardsRefs
			bind:titleHighlight={filterValue}
			class={selectCardClass}
			cards={filteredList}
			{variant}
			{selectedCardsId}
			on:click={(e) => handleCardClick(e.detail)}
			hideCustomCard={hideSelectListCustomCard}
		>
			<slot slot="custom-card" name="select-list-custom-card" />
		</Cards>
	{:else}
		{#each filteredList as room, index}
			<Collapse
				id="collapse-a956jb0"
				class={filteredList.length - 1 === index ? '' : 'mb-s'}
				title={room.roomName}
				expanded={roomsExpanded[room.roomID]}
				on:click={() => {
					roomsExpanded[room.roomID] = !roomsExpanded[room.roomID];
				}}
			>
				{@const hasMissingDevices = room.roomID === 'missing_devices' || room.roomID === -99}
				{#if hasMissingDevices && missingDevicesAlertHeading}
					<Alert type="warning" class="mb-s" heading={missingDevicesAlertHeading} />
				{/if}
				<Cards
					bind:allCardsRefs
					bind:titleHighlight={filterValue}
					class={selectCardClass}
					cards={room.cards}
					{variant}
					{selectedCardsId}
					newLayoutCardBgColor="color-2"
					on:click={(e) => handleCardClick(e.detail)}
				/>
			</Collapse>
		{/each}
	{/if}
{:else if filterValue}
	<NoSearchResults search={filterValue} />
{/if}
